import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Input,
  Alert,
  Chip,
  IconButton,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Grid,
  FormControl,
  FormLabel,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Trash as DeleteIcon,
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  ShieldCheck as SecurityIcon,
  HardDrives as DnsIcon,
  ArrowSquareOut as OpenIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { DNSInstructions } from './DNSInstructions';
import { DomainVerification } from './DomainVerification';

interface Domain {
  id: string;
  host: string;
  type: 'primary' | 'custom';
  verified: boolean;
  ssl_status: 'active' | 'pending' | 'expired' | 'failed';
}

interface DomainManagerProps {
  siteId: number;
}

export const DomainManager: React.FC<DomainManagerProps> = ({ siteId }) => {
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const queryClient = useQueryClient();

  // Fetch domains
  const { data: domainsData, isLoading } = useQuery({
    queryKey: ['cms-domains', siteId],
    queryFn: async () => {
      const response = await api.get(`/api/cms/sites/${siteId}/domains/`);
      console.log('DomainManager Query - Full response:', response);
      console.log('DomainManager Query - response.data:', response.data);
      // The API returns an array of domains directly from the ViewSet action
      return response.data;
    },
  });

  // Add domain mutation
  const addDomainMutation = useMutation({
    mutationFn: async (host: string) => {
      return api.post(`/api/cms/sites/${siteId}/domains/`, { host });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-domains', siteId] });
      setShowAddDomain(false);
      setNewDomain('');
    },
  });

  // Remove domain mutation
  const removeDomainMutation = useMutation({
    mutationFn: async (domainId: string) => {
      return api.delete(`/api/cms/sites/${siteId}/domains/${domainId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-domains', siteId] });
    },
  });

  const handleAddDomain = () => {
    if (newDomain.trim()) {
      addDomainMutation.mutate(newDomain.trim());
    }
  };

  const handleRemoveDomain = (domainId: string) => {
    if (confirm('Are you sure you want to remove this domain?')) {
      removeDomainMutation.mutate(domainId);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'danger';
      case 'failed': return 'danger';
      default: return 'neutral';
    }
  };

  const getStatusIcon = (domain: Domain) => {
    if (domain.verified) {
      return <VerifiedIcon color="var(--joy-palette-success-500)" />;
    } else {
      return <WarningIcon color="var(--joy-palette-warning-500)" />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading domains...</Typography>
      </Box>
    );
  }

  console.log('DomainManager - Raw domainsData:', domainsData);
  console.log('DomainManager - Type of domainsData:', typeof domainsData);
  console.log('DomainManager - Is Array?', Array.isArray(domainsData));
  
  // The API returns an array directly, not an object with domains property
  const domains = Array.isArray(domainsData) ? domainsData : ((domainsData as any)?.domains || []);
  console.log('DomainManager - Extracted domains:', domains);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography level="h3">Custom Domains</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Add your own domain to make your website accessible at your custom URL
          </Typography>
        </Box>
        <Button
          startDecorator={<AddIcon />}
          onClick={() => setShowAddDomain(true)}
          disabled={domains.filter((d: any) => d.type === 'custom').length >= 5}
        >
          Add Domain
        </Button>
      </Box>

      {/* Domains List */}
      <Grid container spacing={2}>
        {domains.map((domain: any) => (
          <Grid xs={12} md={6} key={domain.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(domain)}
                    <Typography level="title-md">{domain.host}</Typography>
                    <Chip
                      size="sm"
                      color={getStatusColor(domain.ssl_status)}
                      variant="soft"
                    >
                      {domain.ssl_status}
                    </Chip>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {domain.type === 'custom' && !domain.verified && (
                      <Button
                        size="sm"
                        variant="soft"
                        onClick={() => {
                          setSelectedDomain(domain);
                          setShowVerification(true);
                        }}
                      >
                        Verify
                      </Button>
                    )}
                    
                    {domain.type === 'custom' && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleRemoveDomain(domain.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip
                    size="sm"
                    variant="outlined"
                    startDecorator={<DnsIcon />}
                  >
                    {domain.type === 'primary' ? 'Primary' : 'Custom'}
                  </Chip>
                  
                  <Chip
                    size="sm"
                    variant="outlined"
                    startDecorator={<SecurityIcon />}
                    color={domain.verified ? 'success' : 'warning'}
                  >
                    {domain.verified ? 'SSL Active' : 'SSL Pending'}
                  </Chip>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<OpenIcon />}
                    onClick={() => window.open(`https://${domain.host}`, '_blank')}
                    disabled={!domain.verified}
                  >
                    Visit Site
                  </Button>
                  
                  {domain.type === 'custom' && (
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => {
                        setSelectedDomain(domain);
                        setShowInstructions(true);
                      }}
                    >
                      DNS Setup
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add Domain Modal */}
      <Modal open={showAddDomain} onClose={() => setShowAddDomain(false)}>
        <ModalDialog>
          <DialogTitle>Add Custom Domain</DialogTitle>
          <DialogContent>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Domain Name</FormLabel>
              <Input
                placeholder="example.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
              />
            </FormControl>
            
            <Alert sx={{ mb: 2 }}>
              <Typography level="body-sm">
                After adding your domain, you'll need to update your DNS settings to point to ApexOS.
              </Typography>
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="plain" onClick={() => setShowAddDomain(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddDomain}
                loading={addDomainMutation.isPending}
                disabled={!newDomain.trim()}
              >
                Add Domain
              </Button>
            </Box>
          </DialogContent>
        </ModalDialog>
      </Modal>

      {/* DNS Instructions Modal */}
      {selectedDomain && (
        <DNSInstructions
          open={showInstructions}
          onClose={() => setShowInstructions(false)}
          domain={selectedDomain}
          siteId={siteId}
        />
      )}

      {/* Domain Verification Modal */}
      {selectedDomain && (
        <DomainVerification
          open={showVerification}
          onClose={() => setShowVerification(false)}
          domain={selectedDomain}
          siteId={siteId}
        />
      )}
    </Box>
  );
};
